/**
 * @deprecated Use AuthGuard from components/auth instead.
 *
 * This component is maintained for backward compatibility.
 * New code should use:
 *
 * @example
 * // Instead of:
 * import RoleGuard from '../components/roles/RoleGuard';
 * <RoleGuard allowedRoles={[USER_ROLES.ADMIN]}><Content /></RoleGuard>
 *
 * // Use:
 * import { AuthGuard } from '../components/auth';
 * <AuthGuard allowedRoles={[USER_ROLES.ADMIN]}><Content /></AuthGuard>
 */

import Image from "next/image";
import LogoIcon from "../../public/logo/logo.png";
import { useMemo, useEffect } from "react";
import { useRouter } from "next/router";
import { useAuth } from "../../contexts/AuthContext";
import { useRole } from "../../contexts/RoleContext";
import { canAccessPage, getRoleName } from "../../utils/roleUtils";
import { USER_ROLES } from "../../lib/supabase";

/**
 * @deprecated Use AuthGuard from components/auth instead
 */
const RoleGuard = ({
  children,
  allowedRoles = [],
  fallbackComponent = null,
}) => {
  const router = useRouter();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { userRole, loading, initialized } = useRole();

  // Memoize access check to prevent unnecessary recalculations
  // NOTE: This hook MUST be called before any early returns to satisfy React's Rules of Hooks
  const hasAccess = useMemo(() => {
    if (!initialized || !userRole) return false;

    // Admins have universal access - bypass all restrictions
    if (userRole === USER_ROLES.ADMIN) return true;

    // Check if user can access current page
    const pageAccess = canAccessPage(userRole, router.pathname);

    // If specific allowed roles are provided, check against them
    let roleAccess = true;
    if (allowedRoles.length > 0) {
      roleAccess = allowedRoles.includes(userRole);
    }

    return pageAccess && roleAccess;
  }, [userRole, router.pathname, allowedRoles, initialized]);

  // If not authenticated, redirect to sign-in
  useEffect(() => {
    if (!authLoading && !isAuthenticated()) {
      router.replace({
        pathname: "/sign-in",
        query: { returnUrl: router.asPath },
      });
    }
  }, [authLoading, isAuthenticated, router]);

  // Show loading while checking auth or if not authenticated (redirect pending)
  if (authLoading || !isAuthenticated()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-light-base dark:bg-jacarta-800">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-light-base dark:bg-jacarta-800">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
          <p className="text-jacarta-500 dark:text-jacarta-300 mt-4">
            Checking access permissions...
          </p>
        </div>
      </div>
    );
  }

  // Show access denied message
  if (!hasAccess) {
    // Use custom fallback component if provided
    if (fallbackComponent) {
      return fallbackComponent;
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-light-base dark:bg-jacarta-800">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="mb-6 flex justify-center">
            <div className="w-16 h-16 relative">
              <Image
                src={LogoIcon}
                alt="Impactoverse Logo"
                fill
                style={{ objectFit: "contain" }}
              />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-jacarta-700 dark:text-white mb-4">
            Access Restricted
          </h1>

          <p className="text-jacarta-500 dark:text-jacarta-300 mb-8">
            Your current role ({getRoleName(userRole)}) doesn&apos;t have permission
            to access this page.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => router.back()}
              className="px-4 py-2 border border-jacarta-300 dark:border-jacarta-600 text-jacarta-600 dark:text-jacarta-300 rounded-lg hover:bg-jacarta-50 dark:hover:bg-jacarta-700 transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={() => router.push("/dashboard")}
              className="px-4 py-2 bg-jacarta-100 dark:bg-jacarta-700 text-jacarta-700 dark:text-white rounded-lg hover:bg-jacarta-200 dark:hover:bg-jacarta-600 transition-colors"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // User has access, render the protected content
  return children;
};

export default RoleGuard;
